import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

interface LayoutProps {
  children: React.ReactNode;
  projectId?: number | null;
  projectName?: string;
  onBack?: () => void;
}

const Layout: React.FC<LayoutProps> = ({ children, projectId: propProjectId, projectName, onBack }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();

  // Use projectId from props if provided, otherwise extract from URL
  const projectId = propProjectId ?? (urlProjectId ? parseInt(urlProjectId) : null);
  const { user, isAdmin, isSuperAdmin, logout } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [contentgenDropdownOpen, setContentgenDropdownOpen] = useState(false);
  const [showProjectAlert, setShowProjectAlert] = useState(false);
  const [fetchedProjectName, setFetchedProjectName] = useState<string>('');
  const navRef = useRef<HTMLElement>(null);

  // Initialize category collapsed state from localStorage (default: all open)
  const [collapsedCategories, setCollapsedCategories] = useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem('collapsedCategories');
    return saved ? JSON.parse(saved) : {};
  });

  // Toggle category collapse state
  const toggleCategory = (categoryId: string) => {
    setCollapsedCategories((prev) => {
      const newState = { ...prev, [categoryId]: !prev[categoryId] };
      localStorage.setItem('collapsedCategories', JSON.stringify(newState));
      return newState;
    });
  };

  // Password change
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');
    if (!currentPassword || !newPassword || !confirmPassword) { setPasswordError('All fields are required'); return; }
    if (newPassword.length < 6) { setPasswordError('New password must be at least 6 characters'); return; }
    if (newPassword !== confirmPassword) { setPasswordError('New passwords do not match'); return; }
    setPasswordLoading(true);
    try {
      await api.put('/auth/me/password', { current_password: currentPassword, new_password: newPassword });
      setPasswordSuccess('Password changed successfully');
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      setTimeout(() => { setShowChangePassword(false); setPasswordSuccess(''); }, 1500);
    } catch (e: any) {
      setPasswordError(e.response?.data?.detail || 'Failed to change password');
    }
    setPasswordLoading(false);
  };

  const navItems = [
    {
      id: 'projects',
      label: 'Projects',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
        </svg>
      ),
      path: projectId ? `/projects/${projectId}` : '/',
      alwaysNavigate: true, // Always navigate even if in project context
    },
    // Onboarding Section
    { id: 'onboarding-header', label: 'Onboarding', isCategory: true },
    {
      id: 'factsagent',
      label: 'Facts Agent',
      category: 'onboarding-header',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/factsagent',
      requiresProject: true,
    },
    {
      id: 'icp-agent',
      label: 'ICP',
      category: 'onboarding-header',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      path: '/icp-agent',
      requiresProject: true,
    },
    {
      id: 'website-audit',
      label: 'Website Audit',
      category: 'onboarding-header',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      path: '/website-audit',
      requiresProject: true,
    },
    {
      id: 'content-calendar',
      label: 'Content Calendar',
      category: 'onboarding-header',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      path: '/content-calendar',
      requiresProject: true,
    },
    // Branding Section
    { id: 'branding-header', label: 'Branding', isCategory: true },
    {
      id: 'brand-kit',
      label: 'Brand Kit',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
      path: '/brand-kit',
      requiresProject: true,
    },
    {
      id: 'imagegenerator',
      label: 'Image Generator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      path: '/imagegenerator',
      requiresProject: true,
    },
    // Content Section
    { id: 'content-header', label: 'Content', isCategory: true },
    {
      id: 'sop-topicalmap',
      label: 'Topical Map',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      path: '/sop-topicalmap',
      requiresProject: true,
    },
    {
      id: 'content-writer',
      label: 'Content Writer',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      path: '/content-writer',
      requiresProject: true,
    },
    {
      id: 'cw-non-bn',
      label: 'CW Non-BN',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        </svg>
      ),
      path: '/cw-non-bn',
      requiresProject: true,
    },
    {
      id: 'contentframeworkv2',
      label: 'Master Framework',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        </svg>
      ),
      path: '/contentframeworkv2',
      requiresProject: true,
    },
    {
      id: 'oldcp-group',
      label: 'CP Images',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      path: '/cpimages',
      requiresProject: true,
    },
    {
      id: 'contentframework-custom',
      label: 'Custom Framework',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
      path: '/contentframework-custom',
      requiresProject: true,
    },
    {
      id: 'briefcontent',
      label: 'Bulk Brief to Content',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/briefcontent',
      requiresProject: true,
    },
    // Publishing Section
    { id: 'publishing-header', label: 'Publishing', isCategory: true },
    {
      id: 'liveupdate',
      label: 'WP Live Update',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      ),
      path: '/liveupdate',
      requiresProject: true,
    },
    {
      id: 'wp-mcp',
      label: 'WP MCP Hub',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M12 5v14M7.05 7.05l9.9 9.9M16.95 7.05l-9.9 9.9" />
        </svg>
      ),
      wpMcpSso: true,
      requiresProject: false,
    },
    {
      id: 'payloadagent',
      label: 'Live Payload Update',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      path: '/payloadagent',
      requiresProject: true,
    },
    {
      id: 'gdriveeditor',
      label: 'Google Drive Editor',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      path: '/gdriveeditor',
      requiresProject: false,
    },
    {
      id: 'faq-injector',
      label: 'FAQ Injector',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      path: '/faq-injector',
      requiresProject: true,
    },
    // SEO Tools Section
    { id: 'seo-header', label: 'SEO Tools', isCategory: true },
    {
      id: 'seo-dashboard',
      label: 'Clients Analytics',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      path: '/seo-dashboard',
      requiresProject: false,
    },
    {
      id: 'holistic-seo',
      label: 'Holistic SEO',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
        </svg>
      ),
      path: '/holistic-seo',
      requiresProject: true,
    },
    {
      id: 'llm-visibility',
      label: 'LLM Visibility',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      ),
      path: '/llm-visibility',
      requiresProject: true,
    },
    {
      id: 'llm-block-test',
      label: 'LLM Block Test',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      ),
      path: '/llm-block-test',
      requiresProject: false,
    },
    {
      id: 'live-html-test',
      label: 'Live HTML Test',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      path: '/live-html-test',
      requiresProject: false,
    },
    {
      id: 'brand-brain',
      label: 'Brand Brain',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
      path: '/brand-brain',
      requiresProject: false,
    },
    {
      id: 'article-update',
      label: 'Article Update Angle',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      path: '/article-update',
      requiresProject: false,
    },
    {
      id: 'baha-seo',
      label: 'Homepage SEO Checker',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      path: '/bahaagents/seo-checker',
      requiresProject: false,
    },
    {
      id: 'baha-pr',
      label: 'Press Release Generator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      path: '/bahaagents/press-release',
      requiresProject: false,
    },
    {
      id: 'baha-gmb',
      label: 'GMB Post Generator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/bahaagents/gmb-post',
      requiresProject: false,
    },
    {
      id: 'baha-schema',
      label: 'Schema Generator',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
        </svg>
      ),
      path: '/bahaagents/schema-generator',
      requiresProject: false,
    },
    {
      id: 'intent-volume',
      label: 'Intent & Volume',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/intent-volume',
      requiresProject: true,
    },
    // Social Media Section
    { id: 'social-header', label: 'Social Media', isCategory: true },
    {
      id: 'social-media',
      label: 'Social Media',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
      ),
      path: '/social-media',
      requiresProject: true,
    },
    // Settings and Knowledge Base (outside categories)
    {
      id: 'settings',
      label: 'Settings',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      path: '/settings',
    },
    {
      id: 'knowledgebase',
      label: 'Knowledge Base',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      path: '/knowledgebase',
    },
  ];

  // Admin-only nav items
  const adminNavItems = [
    {
      id: 'agent-monitor',
      label: 'Agent Monitor',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      ),
      path: '/agent-monitor',
    },
    {
      id: 'system-monitor',
      label: 'System Monitor',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/system-monitor',
    },
    {
      id: 'analytics',
      label: 'Analytics Dashboard',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      path: '/analytics',
    },
    {
      id: 'notion-dashboard',
      label: 'Notion Workspace',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      path: '/notion-dashboard',
    },
    {
      id: 'users',
      label: 'User Management',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      ),
      path: '/users',
    },
    {
      id: 'backup-logs',
      label: 'Ascent Backups',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
        </svg>
      ),
      path: '/backup-logs',
    },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    // Sidebar items can still optionally encode a query string in their
    // `path`. Compare pathname-to-pathname; if a query string is given,
    // every key/value in it must also match the current URL.
    const [bare, queryStr] = path.split('?');
    const pathWithoutLeadingSlash = bare.startsWith('/') ? bare.slice(1) : bare;
    const pathnameMatches =
      location.pathname === bare ||
      location.pathname.startsWith(bare + '/') ||
      location.pathname.includes(`/${pathWithoutLeadingSlash}`);
    if (!pathnameMatches) return false;
    if (queryStr) {
      const wanted = new URLSearchParams(queryStr);
      const have = new URLSearchParams(location.search);
      for (const [k, v] of wanted.entries()) {
        if (have.get(k) !== v) return false;
      }
    }
    return true;
  };

  const isGroupActive = (item: any) => {
    if (item.children) {
      return item.children.some((child: any) => isActive(child.path));
    }
    return item.path ? isActive(item.path) : false;
  };

  // Open the WP MCP Hub (PHP dashboard) through a single-use SSO ticket.
  // The tab is opened synchronously (so browsers don't treat it as a blocked
  // popup), then pointed at the dashboard once the ticket is minted.
  const openWpMcpHub = async () => {
    const tab = window.open('', '_blank', 'noopener,noreferrer');
    try {
      const res = await api.get('/wpmcp/sso-ticket');
      const ticket = res.data?.ticket;
      if (!ticket) throw new Error('No ticket returned');
      const url = `/wp-mcp/?p=sso&ticket=${encodeURIComponent(ticket)}`;
      if (tab) {
        tab.location.href = url;
      } else {
        // Popup was blocked — fall back to same-tab navigation.
        window.location.href = url;
      }
    } catch (err) {
      if (tab) tab.close();
      alert('Could not open the WP MCP Hub. Please make sure you are signed in and try again.');
    }
  };

  const handleNavClick = (item: any) => {
    if (item.isGroup) {
      if (item.id === 'contentgen-group') setContentgenDropdownOpen(!contentgenDropdownOpen);
      return;
    }

    // WP MCP Hub: open the PHP dashboard via a single-use SSO ticket so the
    // user never sees a second login. Opens in a new tab (the dashboard blocks
    // being iframed and runs its own hardened session).
    if (item.wpMcpSso) {
      openWpMcpHub();
      return;
    }

    // Always navigate if alwaysNavigate flag is set (e.g., Projects button)
    if (item.alwaysNavigate) {
      navigate(item.path);
      return;
    }

    if (item.requiresProject && !projectId) {
      // Show alert for project-required items
      setShowProjectAlert(true);
      setTimeout(() => setShowProjectAlert(false), 3000);
      return;
    }
    if (item.requiresProject && projectId) {
      // Use new URL structure: /projects/:projectId/agent-name
      const path = item.path.startsWith('/') ? item.path.slice(1) : item.path;
      navigate(`/projects/${projectId}/${path}`);
    } else if (!item.requiresProject) {
      navigate(item.path);
    }
  };

  const handleChildNavClick = (child: any) => {
    if (child.requiresProject && !projectId) {
      // Show alert for project-required items
      setShowProjectAlert(true);
      setTimeout(() => setShowProjectAlert(false), 3000);
      return;
    }
    if (child.requiresProject && projectId) {
      navigate(`${child.path}?project_id=${projectId}`);
    } else if (!child.requiresProject) {
      navigate(child.path);
    }
  };

  // Scroll active item into view when location changes
  useEffect(() => {
    if (navRef.current) {
      const activeButton = navRef.current.querySelector('.nav-link.active');
      if (activeButton) {
        activeButton.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  }, [location.pathname]);


  // Auto-fetch project name when projectId is set and no name was passed via props
  useEffect(() => {
    let cancelled = false;
    if (projectId && !projectName) {
      api.get(`/projects/${projectId}`)
        .then((res) => {
          if (cancelled) return;
          const data = res.data || {};
          const name = data.name || data.client_name || '';
          if (name) setFetchedProjectName(name);
        })
        .catch(() => { /* ignore; fallback handled in JSX */ });
    } else if (!projectId) {
      setFetchedProjectName('');
    }
    return () => { cancelled = true; };
  }, [projectId, projectName]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-full bg-sidebar transition-all duration-300 z-40 flex flex-col ${
          sidebarCollapsed ? 'w-20' : 'w-64'
        }`}
      >
        {/* Logo */}
        <div className="flex items-center h-16 px-4 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-400 to-brand-600 flex items-center justify-center">
              <span className="text-white font-bold text-lg">BN</span>
            </div>
            {!sidebarCollapsed && (
              <div>
                <h1 className="text-white font-bold text-lg">BrandNorth</h1>
                <p className="text-gray-400 text-xs">Content Platform</p>
              </div>
            )}
          </div>
        </div>

        {/* Scrollable Navigation */}
        <nav ref={navRef} className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item: any, index: number) => {
            const disabled = item.requiresProject && !projectId;

            // Render category header with toggle
            if (item.isCategory) {
              const isCategoryCollapsed = collapsedCategories[item.id];
              return (
                <div key={item.id} className={sidebarCollapsed ? 'hidden' : 'pt-4 mt-2 border-t border-white/10'}>
                  <button
                    onClick={() => toggleCategory(item.id)}
                    className="w-full flex items-center justify-between px-3 py-1 hover:bg-white/5 rounded transition-colors group"
                  >
                    <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold">{item.label}</p>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-500 transition-transform ${isCategoryCollapsed ? '-rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              );
            }

            // Check if item belongs to a collapsed category
            const belongsToCategory = index > 0 && navItems.slice(0, index).reverse().find((prev: any) => prev.isCategory);
            if (belongsToCategory && collapsedCategories[belongsToCategory.id] && !sidebarCollapsed) {
              // Check if next item is a category (meaning this is the last item in current category)
              const nextItem = navItems[index + 1];
              if (!nextItem || nextItem.isCategory) {
                return null; // Skip rendering
              }
              return null; // Skip rendering
            }

            // Render group with dropdown
            if (item.isGroup && item.children) {
              const groupActive = isGroupActive(item);
              const isOpen = contentgenDropdownOpen;
              return (
                <div key={item.id}>
                  <button
                    onClick={() => handleNavClick(item)}
                    disabled={disabled}
                    className={`w-full nav-link ${groupActive ? 'active' : ''} ${
                      disabled ? 'opacity-40 cursor-not-allowed' : ''
                    } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    {item.icon}
                    {!sidebarCollapsed && (
                      <>
                        <span className="flex-1 text-left">{item.label}</span>
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </>
                    )}
                  </button>
                  {(isOpen || sidebarCollapsed) && !disabled && (
                    <div className={`${sidebarCollapsed ? 'absolute left-20 bg-sidebar rounded-lg shadow-lg p-2 min-w-[180px] z-50' : 'ml-4 mt-1 space-y-1'}`}>
                      {item.children.map((child: any) => (
                        <button
                          key={child.id}
                          onClick={() => handleChildNavClick(child)}
                          className={`w-full nav-link text-sm justify-start text-left ${isActive(child.path) ? 'active' : ''} ${
                            sidebarCollapsed ? '' : 'py-1.5'
                          }`}
                          title={sidebarCollapsed ? child.label : undefined}
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-current opacity-50 flex-shrink-0"></span>
                          {!sidebarCollapsed && <span>{child.label}</span>}
                          {sidebarCollapsed && <span>{child.label}</span>}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            // Render normal nav item
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`w-full nav-link ${item.path && isActive(item.path) ? 'active' : ''} ${
                  disabled ? 'opacity-40 cursor-pointer' : ''
                } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                {item.icon}
                {!sidebarCollapsed && <span>{item.label}</span>}
              </button>
            );
          })}

          {/* Super Admin-only navigation */}
          {isSuperAdmin && (
            <>
              {!sidebarCollapsed && (
                <div className="pt-4 mt-4 border-t border-white/10">
                  <button
                    onClick={() => toggleCategory('admin-section')}
                    className="w-full flex items-center justify-between px-3 py-1 hover:bg-white/5 rounded transition-colors group"
                  >
                    <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Admin</p>
                    <svg
                      className={`w-3.5 h-3.5 text-gray-500 transition-transform ${collapsedCategories['admin-section'] ? '-rotate-90' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                </div>
              )}
              {!collapsedCategories['admin-section'] && adminNavItems.map((item: any) => {
                const isDisabled = item.requiresProject && !projectId;
                const itemPath = item.path.replace('{projectId}', projectId?.toString() || '');

                return (
                  <button
                    key={item.id}
                    onClick={() => {
                      if (item.requiresProject && !projectId) {
                        setShowProjectAlert(true);
                        setTimeout(() => setShowProjectAlert(false), 3000);
                        return;
                      }
                      navigate(itemPath);
                    }}
                    className={`w-full nav-link ${isActive(itemPath) ? 'active' : ''} ${
                      isDisabled ? 'opacity-40 cursor-pointer' : ''
                    } ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
                    title={sidebarCollapsed ? item.label : undefined}
                  >
                    {item.icon}
                    {!sidebarCollapsed && <span>{item.label}</span>}
                  </button>
                );
              })}
            </>
          )}
        </nav>

        {/* Pinned Bottom Section */}
        <div className="flex-shrink-0 border-t border-white/10">
          {/* Support & Report */}
          <div className={`${sidebarCollapsed ? 'p-2 space-y-1' : 'px-4 py-3 space-y-1'}`}>
            {!sidebarCollapsed && (
              <p className="px-3 text-xs text-gray-500 uppercase tracking-wider mb-2">Help</p>
            )}
            <a
              href="mailto:support@brandnorth.com"
              className={`w-full nav-link ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
              title={sidebarCollapsed ? 'Support' : 'Email support@brandnorth.com'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              {!sidebarCollapsed && <span>Support</span>}
            </a>
            <a
              href="https://www.notion.so/brandnorth/Bug-Reports-Tasks"
              target="_blank"
              rel="noopener noreferrer"
              className={`w-full nav-link ${sidebarCollapsed ? 'justify-center px-2' : ''}`}
              title={sidebarCollapsed ? 'Report Issue' : 'Report a bug or request a feature on Notion'}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {!sidebarCollapsed && <span>Report Issue</span>}
            </a>
          </div>

          {/* Project Info (when selected) */}
          {projectId && !sidebarCollapsed && (
            <div className="p-4 border-t border-white/10">
              <div className="bg-sidebar-light rounded-lg p-3">
                <p className="text-gray-400 text-xs uppercase tracking-wider mb-1">Current Project</p>
                <p className="text-white font-medium text-sm truncate">{projectName || fetchedProjectName || `Project #${projectId}`}</p>
                {onBack && (
                  <button
                    onClick={onBack}
                    className="mt-2 text-brand-400 hover:text-brand-300 text-xs flex items-center gap-1"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Change Project
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Collapse Toggle */}
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-sidebar-light border border-white/10 rounded-full flex items-center justify-center text-gray-400 hover:text-white transition-colors"
        >
          <svg
            className={`w-4 h-4 transition-transform ${sidebarCollapsed ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </aside>

      {/* Main Content */}
      <main
        className={`flex-1 transition-all duration-300 ${
          sidebarCollapsed ? 'ml-20' : 'ml-64'
        }`}
      >
        {/* Top Bar */}
        <header className="sticky top-0 h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 z-30">
          <div className="flex items-center gap-4">
            {/* Home Button */}
            <button
              onClick={() => navigate('/projects')}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Go to Projects"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
              </svg>
              <span className="text-sm font-medium">Home</span>
            </button>

            {/* Show Activity — visible whenever a project is in scope */}
            {projectId && (
              <button
                onClick={() => navigate(`/projects/${projectId}/activities`)}
                className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:text-brand-700 hover:bg-brand-50 rounded-lg transition-colors"
                title="Show live activity for this project"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span className="text-sm font-medium">Show Activity</span>
              </button>
            )}

            {projectId && projectName && (
              <div className="flex items-center gap-2 text-sm pl-4 border-l border-gray-200">
                <span className="text-gray-400">Project:</span>
                <span className="font-medium text-gray-700">{projectName}</span>
              </div>
            )}
          </div>

          <div className="flex items-center gap-4">
            {/* Search (placeholder) */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                className="w-64 pl-10 pr-4 py-2 bg-gray-100 border-0 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:bg-white transition-all"
              />
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            {/* Notifications (placeholder) */}
            <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <span className="absolute top-1 right-1 w-2 h-2 bg-danger-500 rounded-full"></span>
            </button>

            {/* User Menu */}
            <div className="relative flex items-center gap-3 pl-4 border-l border-gray-200">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 hover:bg-gray-100 rounded-lg px-2 py-1 transition-colors"
              >
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-400 to-accent-500 flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {(user?.name || user?.email || 'U').charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="text-left hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">{user?.name || user?.email}</p>
                  <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)}></div>
                  <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-100">
                      <p className="text-sm font-medium text-gray-900">{user?.name || 'No name'}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded-full ${
                        user?.role === 'admin'
                          ? 'bg-purple-100 text-purple-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {user?.role}
                      </span>
                    </div>
                    <button
                      onClick={() => { setShowChangePassword(true); setShowUserMenu(false); }}
                      className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                      </svg>
                      Change Password
                    </button>
                    <button
                      onClick={() => {
                        logout();
                        navigate('/login');
                      }}
                      className="w-full px-4 py-2 text-left text-sm text-danger-600 hover:bg-danger-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-6">
          {children}
        </div>
      </main>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900">Change Password</h3>
            {passwordError && <div className="bg-red-50 text-red-700 px-3 py-2 rounded text-sm">{passwordError}</div>}
            {passwordSuccess && <div className="bg-green-50 text-green-700 px-3 py-2 rounded text-sm">{passwordSuccess}</div>}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
              <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setShowChangePassword(false)} className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={handleChangePassword} disabled={passwordLoading} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
                {passwordLoading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Project Required Alert Toast */}
      {showProjectAlert && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 animate-fade-in">
          <div className="bg-amber-50 border border-amber-200 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 min-w-[300px]">
            <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-amber-900">Please select a project first</p>
              <p className="text-xs text-amber-700 mt-0.5">Go to Projects and select one to continue</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
